import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Crown, Trophy, UserPlus, Users } from 'lucide-react';

import { useHaptics } from '@/hooks/useHaptics';
import { Loader } from '@/components/common/Loader';
import { ChessStatusBadge } from '@/components/chess/ChessStatusBadge';
import { BracketView } from '@/components/chess/BracketView';
import { getMyLogtoSub } from '@/lib/logto';
import {
  useGetBracketQuery,
  useJoinBracketMutation,
  useLeaveBracketMutation,
  useListChessChallengesQuery,
} from '@/services/qchessApi';
import { classifyQchessError, qchessErrorKey } from '@/services/qchessErrors';
import type { Bracket, TournamentStanding } from '@/types/qchess';
import { cn } from '@/lib/cn';

const TERMINAL = new Set<Bracket['status']>(['complete', 'cancelled']);

/**
 * Knockout bracket detail. Registration → participants + join/leave. Live/
 * complete → "your match" focus card + collapsible tree (BracketView), champion
 * banner + standings on complete. Polls while live and refetches on return to
 * the mini app. Admin (create/start/cancel) lives in the qchess-backoffice, not
 * here — TMA tokens don't carry the `qchess:admin` scope.
 */
export function ChessBracketDetail() {
  const { t } = useTranslation('chess');
  const { id = '' } = useParams<{ id: string }>();
  const me = getMyLogtoSub();

  const [pollMs, setPollMs] = useState(6000);
  const {
    data: b,
    isLoading,
    error,
    refetch,
  } = useGetBracketQuery(id, {
    skip: !id,
    refetchOnMountOrArgChange: true,
    pollingInterval: pollMs,
  });

  useEffect(() => {
    setPollMs(b && TERMINAL.has(b.status) ? 0 : 6000);
  }, [b]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refetch();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refetch]);

  // My current in-progress bracket match → currentGameId for the Play CTA.
  const live = !!b && (b.status === 'in_progress' || b.status === 'ready');
  const challengesQuery = useListChessChallengesQuery(
    live ? { status: 'in_progress' } : undefined,
    { skip: !live, pollingInterval: live ? 6000 : 0 },
  );
  const myGameId = useMemo(() => {
    const list = challengesQuery.data?.challenges ?? [];
    return list.find((c) => c.bracketId === id && c.status === 'in_progress')?.currentGameId ?? null;
  }, [challengesQuery.data, id]);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader />
      </div>
    );
  }
  if (error || !b) {
    return (
      <div className="px-4 pt-6">
        <p className="rounded-md bg-danger/15 px-3 py-2 text-sm text-danger" role="alert">
          {t(qchessErrorKey(classifyQchessError(error)))}
        </p>
      </div>
    );
  }

  const clock = `${Math.round(b.clockLimitSeconds / 60)}+${b.clockIncrementSeconds}`;
  const championName = b.championLogtoUserId
    ? b.participants.find((p) => p.logtoUserId === b.championLogtoUserId)?.lichessUsername
    : undefined;

  return (
    <div className="flex flex-col gap-4 px-4 pb-6 pt-5">
      <header className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand/15 text-brand">
            <Trophy aria-hidden className="h-5 w-5" />
          </span>
          <h1 className="text-xl font-bold text-fg">{b.name || t('bracket.title')}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-[11px] text-fg-muted">
          <ChessStatusBadge status={b.status} variant="bracket" />
          <span>{clock}</span>
          <span aria-hidden>·</span>
          <span>
            {t('bracket.seedingLabel')}: {t(`bracket.seeding.${b.seeding}`)}
          </span>
          <span aria-hidden>·</span>
          <span className="flex items-center gap-1">
            <Users aria-hidden className="h-3 w-3" />
            {t('bracket.players', { count: b.participants.length })}
          </span>
        </div>
      </header>

      {b.status === 'registration' && <RegistrationPanel bracket={b} me={me} />}

      {(b.status === 'ready' || b.status === 'in_progress' || b.status === 'complete') && (
        <>
          {b.status === 'complete' && championName && (
            <section className="flex items-center gap-3 rounded-xl bg-success/15 p-4 text-success">
              <Crown aria-hidden className="h-6 w-6 shrink-0" />
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-wide">
                  {t('bracket.champion')}
                </p>
                <p className="truncate text-base font-bold">{championName}</p>
              </div>
            </section>
          )}

          <BracketView bracket={b} me={me} myGameId={myGameId} />

          {b.status === 'complete' && b.finalStandings.length > 0 && (
            <StandingsTable standings={b.finalStandings} />
          )}
        </>
      )}

      {b.status === 'cancelled' && (
        <p className="rounded-xl bg-bg-secondary px-4 py-3 text-sm text-fg-muted">
          {t('bracket.status.cancelled')}
        </p>
      )}

      <p className="px-1 text-[11px] leading-snug text-fg-muted">{t('disclaimer')}</p>
    </div>
  );
}

/* ── Registration: participants + join/leave ──────────────────────────── */

function RegistrationPanel({ bracket, me }: { bracket: Bracket; me: string | null }) {
  const { t } = useTranslation('chess');
  const haptics = useHaptics();
  const [error, setError] = useState<string | null>(null);

  const [join, { isLoading: joining }] = useJoinBracketMutation();
  const [leave, { isLoading: leaving }] = useLeaveBracketMutation();

  const joined = !!me && bracket.participants.some((p) => p.logtoUserId === me);

  const onJoin = async () => {
    haptics.impact('medium');
    setError(null);
    try {
      await join(bracket._id).unwrap();
      haptics.notification('success');
    } catch (err) {
      haptics.notification('error');
      setError(t(qchessErrorKey(classifyQchessError(err))));
    }
  };
  const onLeave = async () => {
    haptics.selection();
    setError(null);
    try {
      await leave(bracket._id).unwrap();
    } catch (err) {
      haptics.notification('error');
      setError(t(qchessErrorKey(classifyQchessError(err))));
    }
  };

  return (
    <section className="flex flex-col gap-3">
      {joined ? (
        <button
          type="button"
          onClick={() => void onLeave()}
          disabled={leaving}
          className="flex items-center justify-center gap-2 rounded-xl bg-bg-tertiary px-4 py-3 text-sm font-bold uppercase tracking-wide text-fg-muted transition-opacity active:opacity-80 disabled:opacity-50"
        >
          {leaving ? t('bracket.leaving') : t('bracket.leave')}
        </button>
      ) : (
        <button
          type="button"
          onClick={() => void onJoin()}
          disabled={joining}
          className="flex items-center justify-center gap-2 rounded-xl bg-brand px-4 py-3 text-sm font-bold text-brand-fg transition-opacity active:opacity-80 disabled:opacity-50"
        >
          <UserPlus aria-hidden className="h-4 w-4" />
          {joining ? t('bracket.joining') : t('bracket.join')}
        </button>
      )}

      {error && (
        <p className="rounded-md bg-danger/15 px-3 py-2 text-sm text-danger" role="alert">
          {error}
        </p>
      )}

      {bracket.registrationClosesAt && (
        <p className="text-xs text-fg-muted">
          {t('bracket.registrationCloses', {
            date: new Date(bracket.registrationClosesAt).toLocaleString(undefined, {
              dateStyle: 'medium',
              timeStyle: 'short',
            }),
          })}
        </p>
      )}

      <div className="flex flex-col gap-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-fg-muted">
          {t('bracket.participants')} ({bracket.participants.length})
        </h2>
        {bracket.participants.length === 0 ? (
          <p className="py-4 text-center text-sm text-fg-muted">{t('bracket.noParticipants')}</p>
        ) : (
          <ul className="flex flex-col gap-1">
            {bracket.participants.map((p) => (
              <li
                key={p.logtoUserId}
                className={cn(
                  'flex items-center gap-2 rounded-lg bg-bg-secondary px-3 py-2 text-sm',
                  me && p.logtoUserId === me && 'ring-1 ring-brand',
                )}
              >
                <span className="font-semibold text-fg">{p.lichessUsername}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

/* ── Final standings ──────────────────────────────────────────────────── */

function StandingsTable({ standings }: { standings: TournamentStanding[] }) {
  const { t } = useTranslation('chess');
  const sorted = [...standings].sort((a, b) => a.rank - b.rank);
  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-sm font-bold text-fg">{t('bracket.standings')}</h2>
      <div className="overflow-hidden rounded-xl bg-bg-secondary">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-[11px] uppercase tracking-wide text-fg-muted">
              <th className="px-3 py-2 text-left font-semibold">{t('bracket.rank')}</th>
              <th className="px-3 py-2 text-left font-semibold">{t('bracket.player')}</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((row) => (
              <tr key={`${row.rank}-${row.username}`} className="border-b border-border last:border-0">
                <td className="px-3 py-2 font-mono text-fg-muted">{row.rank}</td>
                <td className="px-3 py-2 font-semibold text-fg">{row.username}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
